// Derived obstetric values for a pregnancy record (TrakGene Desktop parity — REQ-C1–C4).
//
// Two independent dating routes, both optional and NEVER blocking (REQ-C3): a pregnancy is
// perfectly valid with nothing but a date, or with an EDD typed straight in and no calculation at
// all. Where an input is missing, the caller shows an explanatory placeholder instead of a value.
//
// THE RULES ARE VERIFIED, NOT INFERRED. The brief asked for them to be confirmed against Desktop
// before implementing; both fall out of the recording exactly, to the day:
//   • SS-05: LMP 04/05/2026 → "LMP EDD 08/02/2027". 2026-05-04 + 280 days = 2027-02-08. ✔
//   • SS-05: same record → "LMP est gestation 14 weeks 0 days", captured 10/08/2026.
//     2026-05-04 → 2026-08-10 is 98 days = 14 weeks 0 days exactly. ✔
// So: EDD = LMP + 280 days (40 weeks), and gestation = whole days since the LMP as of *today*,
// expressed as weeks + days.
//
// Pure + dependency-free → unit-testable, and safe to call on every keystroke for live recompute
// (REQ-C4) without a save.

export const GESTATION_TERM_DAYS = 280; // 40 weeks
const MS_PER_DAY = 86400000;

// Placeholder copy for an un-computable value (REQ-C3 — the field explains itself rather than
// sitting blank or showing a bogus zero).
export const OBSTETRIC_PLACEHOLDER = Object.freeze({
  noLmp: 'No LMP Date Entered',
  noScan: 'No Scan Date/Value Entered'
});

// Parse a date that may arrive as a Date, an ISO string, or a picker value. Normalised to UTC
// midnight so a day count can never be knocked out by a timezone offset or a DST boundary —
// gestation is a whole-day quantity and must not depend on the clock time of day.
export function toUtcDay(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// Whole days from `from` to `to` (negative when `to` precedes `from`).
export function daysBetween(from, to) {
  const a = toUtcDay(from);
  const b = toUtcDay(to);
  if (a == null || b == null) return null;
  return Math.round((b - a) / MS_PER_DAY);
}

// Total days → { weeks, days }. Null for a negative total: a pregnancy cannot have negative
// gestation, and showing "-2 weeks" for a mistyped future LMP would be worse than showing nothing.
export function toWeeksDays(totalDays) {
  if (totalDays == null || Number.isNaN(totalDays) || totalDays < 0)
    return null;
  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 };
}

export function weeksDaysToDays(weeks, days) {
  const w = Number(weeks);
  const d = Number(days);
  const hasW = weeks != null && weeks !== '' && !Number.isNaN(w);
  const hasD = days != null && days !== '' && !Number.isNaN(d);
  if (!hasW && !hasD) return null;
  return (hasW ? w : 0) * 7 + (hasD ? d : 0);
}

// "14 weeks 0 days" — Desktop's wording, including the explicit zero.
export function formatWeeksDays(wd) {
  if (!wd) return '';
  const w = `${wd.weeks} ${wd.weeks === 1 ? 'week' : 'weeks'}`;
  const d = `${wd.days} ${wd.days === 1 ? 'day' : 'days'}`;
  return `${w} ${d}`;
}

export function addDays(date, n) {
  const base = toUtcDay(date);
  if (base == null) return null;
  return new Date(base + n * MS_PER_DAY);
}

// ── Route 1: last menstrual period ───────────────────────────────────────────
export function lmpEdd(lastMenstrualDate) {
  return addDays(lastMenstrualDate, GESTATION_TERM_DAYS);
}

export function lmpGestation(lastMenstrualDate, asOf = new Date()) {
  return toWeeksDays(daysBetween(lastMenstrualDate, asOf));
}

// ── Route 2: dating scan ─────────────────────────────────────────────────────
// The scan establishes a gestation at a known date; today's gestation is that plus the days
// elapsed since, and the EDD is the scan date plus whatever of the 280 days remained.
export function scanGestation(
  initialScanDate,
  atScanWeeks,
  atScanDays,
  asOf = new Date()
) {
  const atScan = weeksDaysToDays(atScanWeeks, atScanDays);
  const elapsed = daysBetween(initialScanDate, asOf);
  if (atScan == null || elapsed == null) return null;
  return toWeeksDays(atScan + elapsed);
}

export function scanEdd(initialScanDate, atScanWeeks, atScanDays) {
  const atScan = weeksDaysToDays(atScanWeeks, atScanDays);
  if (atScan == null) return null;
  return addDays(initialScanDate, GESTATION_TERM_DAYS - atScan);
}

// ── The whole read-only block the editor shows ───────────────────────────────
// Returns display-ready strings, using the placeholder copy when an input is missing, so the form
// can render the block without branching. `asOf` is injectable for tests + deterministic snapshots.
export function deriveObstetrics(record, asOf = new Date()) {
  const rec = record || {};
  const lmpWd = lmpGestation(rec.lastMenstrualDate, asOf);
  const lmpDue = lmpEdd(rec.lastMenstrualDate);
  const scanWd = scanGestation(
    rec.initialScanDate,
    rec.gestationAtScanWeeks,
    rec.gestationAtScanDays,
    asOf
  );
  const scanDue = scanEdd(
    rec.initialScanDate,
    rec.gestationAtScanWeeks,
    rec.gestationAtScanDays
  );
  const hasLmp = toUtcDay(rec.lastMenstrualDate) != null;
  const hasScan =
    toUtcDay(rec.initialScanDate) != null &&
    weeksDaysToDays(rec.gestationAtScanWeeks, rec.gestationAtScanDays) != null;
  return {
    // raw values, for callers that want to format dates themselves
    lmpGestation: lmpWd,
    lmpEdd: lmpDue,
    scanGestation: scanWd,
    scanEdd: scanDue,
    // display strings, placeholder-filled
    lmpGestationText: hasLmp
      ? formatWeeksDays(lmpWd)
      : OBSTETRIC_PLACEHOLDER.noLmp,
    lmpEddText: hasLmp ? lmpDue : OBSTETRIC_PLACEHOLDER.noLmp,
    scanGestationText: hasScan
      ? formatWeeksDays(scanWd)
      : OBSTETRIC_PLACEHOLDER.noScan,
    scanEddText: hasScan ? scanDue : OBSTETRIC_PLACEHOLDER.noScan
  };
}

// ── Gravidity / parity (REQ-A3, Tim's decision 11) ───────────────────────────
// Derived from the pregnancy records as a PREFILL only, and overridable: a clinician may learn of
// pregnancies in consultation that were never entered as discrete records, and must be able to type
// the real number. So the stored value always wins when present.
//
// CAUTION: `parity` is also a CanRisk risk-model input (canriskSerializer). The risk model must
// always read an explicit stored number — never a value derived at export time — or a clinician's
// count would silently change the risk calculation. These helpers exist to PREFILL a form field
// the user then accepts and persists; they must not be called from an export path.
const VIABLE_OUTCOMES = new Set(['liveBirth', 'stillbirth']);

export function derivedGravidity(pregnancies) {
  if (!Array.isArray(pregnancies)) return null;
  return pregnancies.length || null;
}

// Parity counts pregnancies carried to a viable gestation. Counted per PREGNANCY, not per fetus:
// obstetric parity counts births, and a twin delivery is one parous event.
export function derivedParity(pregnancies) {
  if (!Array.isArray(pregnancies)) return null;
  let n = 0;
  for (const p of pregnancies) {
    const fetuses = Array.isArray(p?.fetuses) ? p.fetuses : [];
    const viable = fetuses.length
      ? fetuses.some((f) => VIABLE_OUTCOMES.has(f?.outcome))
      : VIABLE_OUTCOMES.has(p?.outcome);
    if (viable) n += 1;
  }
  return n || null;
}

// The value a form should show: the stored number when the user has one, else the derived count.
export function resolveCount(stored, derived) {
  if (stored != null && stored !== '') return stored;
  return derived ?? '';
}
